import React, { createContext, useState, useEffect, ReactNode, useContext, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Corrected import path assuming lib is at src/lib
import { supabase } from '../../../shared/lib/supabaseClient'; 
// Import specific error types: AuthError (as values)
// Import types using `type`
import { AuthError } from '@supabase/supabase-js';
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';
import { queryClient } from '../../../shared/providers/QueryProvider';

// Define the Profile interface
interface Profile {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  profile_picture_url?: string | null;
  is_premium?: boolean | null;
  deleted_at?: string | null;
  // Add other profile fields if needed
}

// Use Supabase User type directly
// Define the shape of the context state
interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  bootstrapping: boolean;
  profileRefreshing: boolean;
  authActionPending: boolean;
  error: string | null;
  sessionEstablishing: boolean;
  needsReactivation: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  signup: (credentials: { 
    email: string; 
    password: string;
    username?: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  refreshProfile: () => Promise<void>;
  reactivateAccount: () => Promise<void>;
}

// Create the context
const AuthContext = createContext<AuthContextType | null>(null);

// Define props for the provider
interface AuthProviderProps {
  children: ReactNode;
}

// Create the provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [profileRefreshing, setProfileRefreshing] = useState(false);
  const [authActionPending, setAuthActionPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionEstablishing, setSessionEstablishing] = useState(false);
  const [needsReactivation, setNeedsReactivation] = useState(false);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const currentUserIdRef = useRef<string | null>(null);
  const currentTokenRef = useRef<string | null>(null);
  const profileFetchSeqRef = useRef(0);
  const emailConfirmationRedirectedRef = useRef(false);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const applySession = useCallback((nextSession: Session | null, opts?: { force?: boolean }) => {
    const nextUser = nextSession?.user ?? null;
    const nextUserId = nextUser?.id ?? null;
    const nextToken = nextSession?.access_token ?? null;
    const force = opts?.force === true;

    if (!force && currentUserIdRef.current === nextUserId && currentTokenRef.current === nextToken) {
      return false;
    }

    currentUserIdRef.current = nextUserId;
    currentTokenRef.current = nextToken;
    setSession(nextSession);
    setUser(nextUser);
    return true;
  }, []);

  const clearSessionStorage = useCallback(() => {
    try {
      sessionStorage.clear();
    } catch (storageError) {
      console.error("Error clearing sessionStorage:", storageError);
    }
  }, []);

  const clearSignedOutState = useCallback(() => {
    applySession(null, { force: true });
    setProfile(null);
    setNeedsReactivation(false);
    setSessionEstablishing(false);
    setBootstrapping(false);
    setProfileRefreshing(false);
    queryClient.clear();

    try {
      localStorage.clear();
    } catch (storageError) {
      console.error("Error clearing localStorage:", storageError);
    }

    clearSessionStorage();
  }, [applySession, clearSessionStorage]);

  const maybeRedirectAfterEmailConfirmation = useCallback((nextUser: User | null) => {
    if (
      emailConfirmationRedirectedRef.current ||
      !nextUser?.email_confirmed_at ||
      window.location.pathname !== '/'
    ) {
      return;
    }

    emailConfirmationRedirectedRef.current = true;
    window.setTimeout(() => {
      navigateRef.current('/portfolio');
    }, 500);
  }, []);

  // Function to fetch profile from Flask backend - uses service role key (never expires)
  const fetchProfile = useCallback(async (userId: string) => {
    const fetchSeq = profileFetchSeqRef.current + 1;
    profileFetchSeqRef.current = fetchSeq;
    setProfileRefreshing(true);
    try {
      // Get current session to extract access token
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      
      if (!currentSession?.access_token) {
        console.error('❌ No access token available for profile fetch');
        if (profileFetchSeqRef.current === fetchSeq && currentUserIdRef.current === userId) {
          setProfile(null);
        }
        return;
      }

      // Dynamic import to avoid circular dependencies and get BASE_URL
      const apiModule = await import('../../../shared/lib/api');
      const BASE_URL = apiModule.BASE_URL;

      // Call Flask backend which uses service role key (bypasses RLS, never expires)
      // Backend will automatically create profile if it doesn't exist
      const response = await fetch(`${BASE_URL}/settings/profile`, {
        headers: {
          'Authorization': `Bearer ${currentSession.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Try to get error message, but handle HTML responses
        const contentType = response.headers.get('content-type');
        let errorData;
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          const text = await response.text();
          console.error(`❌ Backend returned HTML instead of JSON (status ${response.status}):`, text.substring(0, 200));
          errorData = { error: `Backend error (${response.status}): ${response.statusText}. Is Flask backend running?` };
        }
        console.error('❌ Error fetching profile from backend:', errorData);
        if (profileFetchSeqRef.current === fetchSeq && currentUserIdRef.current === userId) {
          setProfile(null);
        }
        return;
      }

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('❌ Backend returned non-JSON response:', text.substring(0, 200));
        console.error('❌ Response status:', response.status, response.statusText);
        console.error('❌ Content-Type:', contentType);
        if (profileFetchSeqRef.current === fetchSeq && currentUserIdRef.current === userId) {
          setProfile(null);
        }
        return;
      }

      const profileData = await response.json();
      if (profileFetchSeqRef.current !== fetchSeq || currentUserIdRef.current !== userId) {
        return;
      }
      
      // Check if portfolio is deleted (soft-deleted)
      if (profileData.deleted_at) {
        console.log('🔄 Portfolio is deleted, showing reactivation prompt');
        setProfile(profileData as Profile);
        setNeedsReactivation(true);
      } else {
        setProfile(profileData as Profile);
        setNeedsReactivation(false);
      }
    } catch (error: any) {
      console.error('❌ Unexpected error fetching profile:', error);
      if (profileFetchSeqRef.current === fetchSeq && currentUserIdRef.current === userId) {
        setProfile(null);
      }
    } finally {
      if (profileFetchSeqRef.current === fetchSeq) {
        setProfileRefreshing(false);
      }
    }
  }, []);

  // Effect to check initial session and listen for auth changes
  useEffect(() => {
    // Check for hash tokens in URL (from email confirmation or password reset)
    const hash = window.location.hash;
    const hasHashTokens = hash && (hash.includes('access_token') || hash.includes('type='));
    
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('❌ Error getting session:', error);
        setError("Failed to load user session.");
        setBootstrapping(false);
        return;
      }
      
      applySession(session, { force: true });
      setBootstrapping(false);

      // Fetch profile directly - backend uses service role key so no JWT expiry issues
      if (session?.user) {
        void fetchProfile(session.user.id);
      }
      
      // Clear hash from URL after processing (if it was there)
      if (hasHashTokens && session) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }

      if (hasHashTokens) {
        maybeRedirectAfterEmailConfirmation(session?.user ?? null);
      }
    }).catch((err) => {
      console.error("Error getting initial session:", err);
      setError("Failed to load user session.");
      setBootstrapping(false);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        const previousUserId = currentUserIdRef.current;
        const nextUser = session?.user ?? null;
        const nextUserId = nextUser?.id ?? null;

        switch (event) {
          case 'SIGNED_OUT':
            // Immediately clear React state to stop any API calls and prevent data leakage.
            clearSignedOutState();
            return;

          case 'SIGNED_IN': {
            const isNewUserSession = Boolean(nextUserId && nextUserId !== previousUserId);
            const isDifferentKnownUser = Boolean(previousUserId && nextUserId && nextUserId !== previousUserId);
            applySession(session);
            setBootstrapping(false);

            if (!nextUser) {
              setProfile(null);
              return;
            }

            if (!isNewUserSession) {
              return;
            }

            // Clear React Query cache when a genuinely different user signs in.
            queryClient.clear();
            if (isDifferentKnownUser) {
              clearSessionStorage();
            }
            setSessionEstablishing(true);
            window.setTimeout(() => {
              setSessionEstablishing(false);
              if (currentUserIdRef.current === nextUser.id) {
                void fetchProfile(nextUser.id);
              }
            }, 100);
            maybeRedirectAfterEmailConfirmation(nextUser);
            return;
          }

          case 'TOKEN_REFRESHED':
          case 'PASSWORD_RECOVERY':
          case 'INITIAL_SESSION':
            applySession(session, { force: event === 'INITIAL_SESSION' });
            setBootstrapping(false);
            if (!session) {
              setProfile(null);
              setNeedsReactivation(false);
            }
            return;

          case 'USER_UPDATED':
            applySession(session, { force: true });
            setBootstrapping(false);
            if (nextUser) {
              void fetchProfile(nextUser.id);
            }
            return;

          default:
            applySession(session);
            setBootstrapping(false);
            if (!session) {
              setProfile(null);
              setNeedsReactivation(false);
            }
        }
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [applySession, clearSessionStorage, clearSignedOutState, fetchProfile, maybeRedirectAfterEmailConfirmation]); 

  // Wrap auth functions in useCallback for stable references
  const login = useCallback(async (credentials: { email: string; password: string }) => {
    setAuthActionPending(true);
    setError(null);
    try {
      // Check if input is email (contains @) or username
      const input = credentials.email.trim();
      let emailToUse = input;
      
      // If input doesn't contain @, treat it as username and look up email
      if (!input.includes('@')) {
        try {
          // Dynamic import to avoid circular dependencies
          const apiModule = await import('../../../shared/lib/api');
          const BASE_URL = apiModule.BASE_URL;
          const response = await fetch(`${BASE_URL}/auth/get-email-by-username/${encodeURIComponent(input)}`);
          
          if (!response.ok) {
            if (response.status === 404) {
              throw new Error('Username not found');
            }
            throw new Error('Failed to look up username');
          }
          
          const data = await response.json();
          if (!data.email) {
            throw new Error('Username not found');
          }
          
          emailToUse = data.email;
        } catch (err: any) {
          const message = err.message || 'Invalid username or email';
          setError(message);
          setAuthActionPending(false);
          return;
        }
      }
      
      // Sign in with email (either provided or looked up from username)
      const { data, error }: { data: { session: Session | null; user: User | null }; error: AuthError | null } = await supabase.auth.signInWithPassword({
        email: emailToUse,
        password: credentials.password,
      });
      
      if (error) {
        // Check for email not confirmed error
        if (error.message?.toLowerCase().includes('email not confirmed')) {
          throw new Error('Please verify your email address. Check your inbox for the confirmation link we sent when you signed up.');
        }
        throw error;
      }
      
      console.log("Login successful, session:", data.session);
      navigate('/portfolio'); 
    } catch (error: any) { 
      console.error('Login error:', error);
      
      // Provide user-friendly error messages
      let message = 'Invalid login credentials.';
      
      if (error instanceof AuthError || error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        
        // Check for various error scenarios
        if (errorMsg.includes('email not confirmed') || errorMsg.includes('verify your email')) {
          message = error.message; // Use the custom message we set above
        } else if (errorMsg.includes('invalid login credentials') || errorMsg.includes('invalid email or password')) {
          message = 'Invalid email/username or password. Please try again.';
        } else if (errorMsg.includes('email')) {
          message = error.message;
        } else {
          message = error.message;
        }
      }
      
      setError(message);
    } finally {
      setAuthActionPending(false);
    }
  }, [navigate]); // Add dependencies like navigate

  const signup = useCallback(async (credentials: { 
    email: string; 
    password: string;
    username?: string;
    firstName?: string;
    lastName?: string;
  }) => {
    setAuthActionPending(true);
    setError(null);
    
    console.log('🔵 Signup initiated:', { email: credentials.email, hasUsername: !!credentials.username });
    
    try {
      // Get current timestamp for terms/privacy acceptance
      // This records the ACTUAL time the user checked the acceptance box
      const acceptanceTimestamp = new Date().toISOString();
      
      // Sign up with Supabase Auth
      // Note: Profile creation happens after email confirmation when user first logs in
      // This is handled by the backend /api/auth/create-profile endpoint or fetchProfile fallback
      const { data, error }: { data: { user: User | null; session: Session | null }; error: AuthError | null } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: {
            username: credentials.username,
            first_name: credentials.firstName,
            last_name: credentials.lastName,
            // Store terms/privacy acceptance timestamps for compliance
            // These will be used when creating the profile after email confirmation
            terms_accepted_at: acceptanceTimestamp,
            terms_version: '1.0',
            privacy_accepted_at: acceptanceTimestamp,
            privacy_version: '1.0',
          }
        }
      });
      
      if (error) {
        console.error('❌ Supabase signup error:', error);
        throw error;
      }
      
      console.log('✅ Signup successful, user created:', { 
        userId: data.user?.id, 
        email: data.user?.email,
        emailConfirmed: data.user?.email_confirmed_at ? 'yes' : 'no (awaiting confirmation)'
      });
      
      // Note: Profile will be created automatically when user:
      // 1. Confirms email via link
      // 2. Logs in for the first time
      // 3. fetchProfile() is called and detects missing profile
      // 4. Backend creates profile using service role key (bypasses RLS)
      
      // Don't throw - signup succeeded, profile creation happens later
    } catch (error: any) {
      console.error('❌ Signup error:', error);
      const message = (error instanceof AuthError || error instanceof Error) ? error.message : 'Could not sign up user.';
      setError(message);
      throw error; // Re-throw so SignUpPage can handle it
    } finally {
      setAuthActionPending(false);
    }
  }, []); // No external dependencies

  const logout = useCallback(async () => {
    setAuthActionPending(true);
    setError(null);
    
    let signOutSucceeded = false;
    
    try {
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        // Check if error is due to missing/invalid session (common after password change)
        const isSessionMissing = 
          signOutError.message?.toLowerCase().includes('session missing') ||
          signOutError.message?.toLowerCase().includes('auth session missing') ||
          signOutError.name === 'AuthSessionMissingError';
        
        if (isSessionMissing) {
          signOutSucceeded = true; // Treat as success since session is already gone
        } else {
          throw signOutError;
        }
      } else {
        signOutSucceeded = true;
      }
    } catch (error: any) {
      // Check if error is due to missing/invalid session
      const isSessionMissing = 
        error?.message?.toLowerCase().includes('session missing') ||
        error?.message?.toLowerCase().includes('auth session missing') ||
        error?.name === 'AuthSessionMissingError' ||
        (error?.status === 403 && error?.message?.toLowerCase().includes('forbidden'));
      
      if (isSessionMissing) {
        signOutSucceeded = true; // Treat as success since session is already gone
      } else {
        console.error('Error during signOut:', error);
      }
    }
    
    // Always clear local state and redirect, even if Supabase signOut failed
    // This handles cases where the session is already invalid (e.g., after password change)
    try {
      // 1. Call signOut first (if not already done)
      if (!signOutSucceeded) {
        try {
          await supabase.auth.signOut();
        } catch (e) {
          // Ignore errors, proceed with cleanup
        }
      }
      
      // 2. Clear all app/browser state that can hold user data
      clearSignedOutState();
      
      // 3. Small delay to ensure cleanup completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // 4. Navigate to login page
      navigate('/login');
      
      if (signOutSucceeded) {
        setError(null); // Clear errors on successful logout
      }
    } catch (cleanupError) {
      console.error("Error during logout cleanup:", cleanupError);
      // Still navigate even if cleanup has issues
      navigate('/login');
    } finally {
      setBootstrapping(false);
      setAuthActionPending(false);
    }
  }, [navigate, clearSignedOutState]);

  const resetPassword = useCallback(async (email: string) => {
    setAuthActionPending(true);
    setError(null);
    try {
      const { error }: { error: AuthError | null } = await supabase.auth.resetPasswordForEmail(email, {});
      if (error) throw error;
       alert('Password reset email sent! Please check your inbox.');
       console.log("Password reset email initiated for:", email);
    } catch (error: any) {
      console.error('Password reset error:', error);
      const message = (error instanceof AuthError || error instanceof Error) ? error.message : 'Could not send password reset email.';
      setError(message);
    } finally {
      setAuthActionPending(false);
    }
  }, []); // No external dependencies

  const updatePassword = useCallback(async (newPassword: string) => {
      if (!user) {
          setError("User not authenticated to update password.");
          return;
      }
      setAuthActionPending(true);
      setError(null);
      try {
         const { data, error }: { data: { user: User | null }; error: AuthError | null } = await supabase.auth.updateUser({ password: newPassword });
         if (error) throw error;
         alert('Password updated successfully!');
         console.log("Password updated successfully for user:", data.user);
         navigate('/portfolio');
      } catch (error: any) { 
          console.error('Password update error:', error);
          const message = (error instanceof AuthError || error instanceof Error) ? error.message : 'Could not update password.';
          setError(message);
      } finally {
          setAuthActionPending(false);
      }
  }, [user, navigate]); // Depends on user and navigate

  // Expose refreshProfile so components can manually refresh profile data
  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  // Reactivate a deleted portfolio
  const reactivateAccount = useCallback(async () => {
    if (!session?.access_token) {
      throw new Error('No active session');
    }

    try {
      // Dynamic import to avoid circular dependencies and get BASE_URL
      const apiModule = await import('../../../shared/lib/api');
      const BASE_URL = apiModule.BASE_URL;

      const response = await fetch(`${BASE_URL}/settings/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to reactivate portfolio' }));
        throw new Error(errorData.error || 'Failed to reactivate portfolio');
      }

      // Refresh profile to clear deleted_at
      if (user?.id) {
        await fetchProfile(user.id);
      }
      
      // Navigate to portfolio
      navigate('/portfolio');
    } catch (error) {
      console.error('Error reactivating portfolio:', error);
      throw error;
    }
  }, [session, user, fetchProfile, navigate]);

  // Memoize the context value including the profile state and stable functions
  const value = useMemo(() => ({
    session,
    user,
    profile,
    loading: bootstrapping,
    bootstrapping,
    profileRefreshing,
    authActionPending,
    error,
    sessionEstablishing,
    needsReactivation,
    login,
    signup,
    logout,
    resetPassword,
    updatePassword,
    refreshProfile,
    reactivateAccount,
  }), [session, user, profile, bootstrapping, profileRefreshing, authActionPending, error, sessionEstablishing, needsReactivation, login, signup, logout, resetPassword, updatePassword, refreshProfile, reactivateAccount]); 

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) { // Check for null instead of undefined
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


export default AuthContext;
